import {Alert, Badge, Button, Group, Paper, Select, Stack, Text, Title} from "@mantine/core";
import {useMutation, useQuery} from "@tanstack/react-query";
import {DateTime} from "luxon";
import {useContext, useEffect, useMemo, useState} from "react";
import {useParams} from "react-router";
import {WarningIcon} from "@phosphor-icons/react";
import withRouteErrorBoundary from "../../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import LoaderScreen from "../../../components/Loader/LoaderScreen.tsx";
import Page from "../../../components/Page/Page.tsx";
import MessageContext from "../../../context/MessageContext.tsx";
import api from "../../../utils/api.ts";
import {numFormat} from "../../../utils/formatters.ts";
import ProductionModuleNav from "./ProductionModuleNav.tsx";

interface BillingInfo {
    annualPrice: number;
    billingExpire: string | null;
    expired: boolean;
    paymentLink: string | null;
}

interface SettingsResponse {
    label?: string;
    settings: Record<string, unknown>;
    isOwner: boolean;
    ownerType?: string;
    owner?: string;
    syncUsers?: Array<{
        uid: string;
        handle: string;
        hasRequiredScopes: boolean;
        missingScopes: string[];
    }>;
    billing?: BillingInfo;
}

function formatDate(iso: string | null): string {
    if (!iso) {
        return '';
    }
    return DateTime.fromISO(iso).toLocaleString(DateTime.DATE_MED);
}

function ProductionModuleSettingsPage() {
    const {moduleId} = useParams<{moduleId?: string}>();
    const {success, error} = useContext(MessageContext);
    const [syncUserId, setSyncUserId] = useState<string | null>(null);

    const settingsQuery = useQuery<SettingsResponse>({
        queryKey: ['prod-module', moduleId, 'settings'],
        queryFn: async () => (await api.get(`/modules/production/${moduleId}/settings`)).data as SettingsResponse,
        enabled: !!moduleId,
    });

    const saveSettingsMutation = useMutation({
        mutationFn: async () => (await api.post(`/modules/production/${moduleId}/settings`, {syncUserId})).data,
        onSuccess: () => {
            success('Settings saved');
            settingsQuery.refetch();
        },
        onError: (e: any) => error('Could not save settings', e?.response?.data?.error ?? e.message),
    });

    const checkPaymentMutation = useMutation({
        mutationFn: async () => (await api.get(`/modules/${moduleId}/billing`, {params: {scan: 1}})).data,
        onSuccess: () => {
            success('Payment status refreshed');
            settingsQuery.refetch();
        },
        onError: (e: any) => error('Could not check for payment', e?.response?.data?.error ?? e.message),
    });

    const ownerType = settingsQuery.data?.ownerType ?? 'user';
    const isOwner = settingsQuery.data?.isOwner ?? false;
    const billing = settingsQuery.data?.billing;

    const syncUserOptions = useMemo(() => {
        if (ownerType !== 'faction') {
            return [];
        }
        const users = settingsQuery.data?.syncUsers ?? [];
        return [...users]
            .sort((a, b) => a.handle.localeCompare(b.handle))
            .map((entry) => ({
                value: entry.uid,
                label: `${entry.handle} (${entry.hasRequiredScopes ? 'API ready' : `Missing: ${entry.missingScopes.join(', ')}`})`,
                disabled: !entry.hasRequiredScopes,
            }));
    }, [ownerType, settingsQuery.data?.syncUsers]);

    useEffect(() => {
        const data = settingsQuery.data;
        if (!data) {
            return;
        }
        if ((data.ownerType ?? 'user') === 'user') {
            setSyncUserId(null);
            return;
        }
        const initial = data.settings?.['syncUserId'];
        setSyncUserId(typeof initial === 'string' ? initial : null);
    }, [settingsQuery.data]);

    if (!moduleId) {
        return <Page><Alert color="red" title="Missing module ID">No module selected.</Alert></Page>;
    }
    if (settingsQuery.isPending) {
        return <LoaderScreen message="Loading settings"/>;
    }
    if (settingsQuery.isError || !settingsQuery.data) {
        return <Page><Alert color="red" title="Settings unavailable">Could not load module settings.</Alert></Page>;
    }

    const renderBillingStatus = () => {
        if (!billing || billing.annualPrice <= 0) {
            return <Badge color="blue" variant="light">Free — no subscription required</Badge>;
        }
        if (!billing.billingExpire) {
            return <Badge color="green" variant="light">Active (no expiry)</Badge>;
        }
        if (billing.expired) {
            return <Badge color="red" variant="light">Expired on {formatDate(billing.billingExpire)}</Badge>;
        }
        return <Badge color="green" variant="light">Active until {formatDate(billing.billingExpire)}</Badge>;
    };

    return (
        <Page>
            <title>{`${settingsQuery.data?.label || 'Production'} - Settings`}</title>
            <Stack gap="md">
                <Title order={1}>{settingsQuery.data?.label || 'Production'} - Settings</Title>
                <ProductionModuleNav moduleId={moduleId}/>

                {!isOwner ? (
                    <Alert color="blue" title="Read-only">
                        Only the module owner can change settings or manage the subscription.
                    </Alert>
                ) : (
                    <>
                        <Paper withBorder p="md">
                            <Text fw={700} mb="sm">Subscription</Text>
                            <Group justify="space-between" align="center" mb="sm">
                                <Text size="sm" c="dimmed">Status</Text>
                                {renderBillingStatus()}
                            </Group>
                            {billing && billing.annualPrice > 0 ? (
                                <>
                                    <Group justify="space-between" align="center" mb="sm">
                                        <Text size="sm" c="dimmed">Annual price</Text>
                                        <Text fw={600}>{numFormat.format(billing.annualPrice)} credits</Text>
                                    </Group>
                                    {billing.expired ? (
                                        <Alert color="red" icon={<WarningIcon/>} mb="sm" title="Subscription expired">
                                            This module is locked until the subscription is renewed. Send the payment in-game,
                                            then use “Check for payment” to refresh the status.
                                        </Alert>
                                    ) : null}
                                    <Group>
                                        {billing.paymentLink ? (
                                            <Button component="a" href={billing.paymentLink} target="_blank" rel="noreferrer">
                                                Pay / Renew in-game
                                            </Button>
                                        ) : null}
                                        <Button
                                            variant="light"
                                            loading={checkPaymentMutation.isPending}
                                            onClick={() => checkPaymentMutation.mutate()}
                                        >
                                            Check for payment
                                        </Button>
                                    </Group>
                                    <Text size="xs" c="dimmed" mt="sm">
                                        Pay by sending {numFormat.format(billing.annualPrice)} credits in-game using the link above —
                                        the payment code is filled in automatically. Renewing early adds a year on top of the
                                        remaining time.
                                    </Text>
                                </>
                            ) : (
                                <Text size="sm" c="dimmed">This module has no subscription fee.</Text>
                            )}
                        </Paper>

                        <Paper withBorder p="md">
                            <Text fw={700} mb="sm">Data Sync</Text>
                            {ownerType === 'faction' ? (
                                <>
                                    {settingsQuery.data.settings?.['syncUserId'] == null && (
                                        <Text c="red" mb="sm">
                                            <WarningIcon/> You need to select a user to sync the data. They must have granted the
                                            correct API permissions.
                                        </Text>
                                    )}
                                    <Group align="end">
                                        <Select
                                            style={{flex: 1}}
                                            label="Sync User"
                                            placeholder="Select faction user"
                                            value={syncUserId}
                                            onChange={setSyncUserId}
                                            data={syncUserOptions}
                                            searchable
                                            clearable
                                        />
                                        <Button loading={saveSettingsMutation.isPending} onClick={() => saveSettingsMutation.mutate()}>
                                            Save
                                        </Button>
                                    </Group>
                                </>
                            ) : (
                                <Text size="sm" c="dimmed">Personal modules always sync using the owner token.</Text>
                            )}
                        </Paper>
                    </>
                )}
            </Stack>
        </Page>
    );
}

export default withRouteErrorBoundary(ProductionModuleSettingsPage);
